import {useForm} from 'react-hook-form';
import {LoadingButton} from '@mui/lab';
import {Box, IconButton, Stack, TextField, Typography} from '@mui/material';

import FormProvider, {RHFAutocomplete, RHFTextField} from '@/commons/hook-form';

import {Icon} from '@iconify/react';
import {useCashRegisterContext} from '@/hooks/useCashRegisterContext';
import {useTheme} from '@mui/material/styles';
import {useEffect, useMemo, useState} from 'react';
import {AutocompleteLabels} from '@/modules/orders/form/order-detail/products-items-list';
import {Order, OrderDetail, OrdersDetail} from '@/modules/orders/types';
import {OrderHistory, translates} from '@/utils';
import {useOrder} from '@/hooks/useOrder';
import {Iconic} from '@/commons/iconic';
import {useCurrentOrder} from "@hooks/useCurrentOrder";
import {defaultValuesForm} from './initial-values';

type Props = {
    current?: any;
    isEdit?: boolean;
    isLoading?: boolean;
    onSave: (values: any) => Promise<void>;
};
export default function PopMoveProducts({isEdit = false, onSave, isLoading, current}: Props) {
    const {isOpen} = useCashRegisterContext();
    const {palette} = useTheme();
    const {id} = current ?? {};
    const [maxQuantity, setMaxQuantity] = useState(0);


    const defaultValues = useMemo(() => defaultValuesForm(id), [id]);

    const methods = useForm<any>({
        defaultValues,
    });

    const {reset, handleSubmit, control, watch, setValue} = methods;

    const {data: dataOrders} = useOrder({
        filter: true,
        query: OrderHistory.OPEN,
    });

    const {data: dataCurrent, ordersDetailsHashMap} = useCurrentOrder(current);

    const onSubmit = async (data: any) => {
        const moveProductSignature = {
            quantity: data.quantity,
            consumerId: data.orders?.value,
            orderDetailId: data.product?.value,
            emitId: id,
        };
        await onSave(moveProductSignature);
    };

    useEffect(() => {
        if ((isEdit && id) || !isEdit) {
            reset(defaultValues as unknown as Partial<any>);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, id]);

    const ordersWithoutCurrent = dataOrders?.data?.filter?.((item: Order) => item.id !== id) ?? [];

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{justifyContent: 'center', display: 'flex', mt: isOpen ? 2 : 0}}>
                <Icon icon="material-symbols:production-quantity-limits" color={palette.info.main} width={100}/>
            </Box>
            <Stack sx={{px: 3, gap: 2, py: 2}} style={{display: 'flex', flexDirection: 'column'}}>
                <RHFAutocomplete
                    name="product"
                    size="medium"
                    label="Producto"
                    options={
                        dataCurrent?.ordersDetails?.map?.((item: OrdersDetail) => ({
                            value: item.id,
                            label: item.product?.product?.name,
                        })) || []
                    }
                    onChangeCustom={(dataSelected: { value: any, label: any }) => {
                        const quantity = ordersDetailsHashMap[dataSelected?.value]?.quantity ?? 0;
                        setValue('quantity', quantity)
                        setMaxQuantity(quantity)
                    }}
                    isOptionEqualToValue={(option: AutocompleteLabels, selected: AutocompleteLabels) =>
                        option.value === selected.value
                    }

                    fullWidth
                />
                <Stack justifyItems="center" alignItems="center">
                    <Typography variant="h5">{translates.quantity}</Typography>

                    <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                        <IconButton onClick={() => {
                            const prevQuantity = watch('quantity');
                            const quantity = prevQuantity <= 0 ? prevQuantity : prevQuantity - 1;
                            setValue('quantity', quantity)
                            if (quantity <= 0) {
                                setValue('product', undefined)
                            }
                        }}>
                            <Iconic icon="eva:minus-fill"/>
                        </IconButton>
                        <RHFTextField
                            sx={{width: '1rem', textAlign: 'center'}}
                            type="number"
                            size="small"
                            variant="standard"
                            name="quantity"
                            control={control}
                        />
                        <IconButton onClick={() => {
                            const prevQuantity = watch('quantity');
                            const quantity = prevQuantity < maxQuantity ? prevQuantity + 1 : prevQuantity;
                            setValue('quantity', quantity)
                        }}>
                            <Iconic icon="eva:plus-fill"/>
                        </IconButton>
                    </Stack>
                </Stack>
                <RHFAutocomplete
                    name="orders"
                    size="medium"
                    label="Cuentas abiertas"
                    options={
                        ordersWithoutCurrent?.map?.((item: Order) => ({
                            value: item.id,
                            label: item.table,
                        })) || []
                    }
                    isOptionEqualToValue={(option: AutocompleteLabels, selected: AutocompleteLabels) =>
                        option.value === selected.value
                    }
                    fullWidth
                />
            </Stack>
            <Stack sx={{justifyContent: 'center', my: 2}}>
                <LoadingButton type="submit" variant="outlined" loading={isLoading}>
                    Mover producto
                </LoadingButton>
            </Stack>
        </FormProvider>
    );
}
